import React from "react";

interface SettingGroupProps {
  title: string;
  /** Compteur 💡 affiché en pill amber à côté du titre (Suggestions only) */
  hintBadge?: number;
  /** Tone du groupe — détermine couleur du titre */
  tone?: "neutral" | "warn" | "muted";
  /** Collapsable (Avancé) — collapsed par défaut */
  collapsible?: boolean;
  defaultCollapsed?: boolean;
  children: React.ReactNode;
  /** testid override (utile pour distinguer "important"/"hint"/"advanced") */
  testid?: string;
}

/**
 * Container groupé pour `SettingRow`s. Affiche le titre uppercase + badge
 * optionnel "N 💡" pour les Suggestions. Cf. Pre-publish Modal B.html
 * lignes 155-176.
 */
const SettingGroup: React.FC<SettingGroupProps> = ({
  title,
  hintBadge,
  tone = "neutral",
  collapsible = false,
  defaultCollapsed = false,
  children,
  testid,
}) => {
  const [collapsed, setCollapsed] = React.useState(
    collapsible && defaultCollapsed,
  );

  const showBadge = typeof hintBadge === "number" && hintBadge > 0;

  return (
    <div
      data-testid={testid ?? "setting-group"}
      data-tone={tone}
      className="space-y-2"
    >
      <button
        type="button"
        onClick={() => collapsible && setCollapsed((v) => !v)}
        disabled={!collapsible}
        className={`flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.7px] ${
          tone === "warn" ? "text-amber-400" : "text-muted-foreground/70"
        } ${collapsible ? "cursor-pointer hover:text-foreground" : "cursor-default"}`}
      >
        <span>{title}</span>
        {showBadge && (
          <span
            data-testid="setting-group-badge"
            className="inline-flex items-center rounded px-1.5 py-px text-[9px] font-bold bg-amber-500/22 text-amber-400"
          >
            {hintBadge} 💡
          </span>
        )}
        {collapsible && (
          <span aria-hidden className="ml-auto text-[10px]">
            {collapsed ? "▾" : "▴"}
          </span>
        )}
      </button>

      {!collapsed && (
        <div className="rounded-[10px] border border-border bg-muted/20 p-3 space-y-2">
          {children}
        </div>
      )}
    </div>
  );
};

export default SettingGroup;
